export interface PerformanceRate { numerator: number; denominator: number; value: number | null }
export const metricKeys = ['lateness', 'absence', 'summaryCompletion', 'summaryTimeliness', 'cancellation', 'momentSent', 'momentReply'] as const
export type MetricKey = typeof metricKeys[number]
export interface PerformanceRecord {
  appointmentId: string
  classStartTime: number
  classStartLocal: string
  outcome: 'COMPLETED' | 'STUDENT_ABSENT' | 'TEACHER_ABSENT' | 'TEACHER_CANCELLED'
  lateSeconds: number | null
  late: boolean | null
  summaryRequired: boolean
  summarySubmitted: boolean
  summaryTiming: 'NOT_REQUIRED' | 'UNKNOWN' | 'ON_TIME' | 'LATE' | 'PENDING' | 'MISSING'
  summaryFirstSubmittedAt: number | null
  summaryDueAt: number | null
  cancelReason: string | null
}
export interface TeacherPerformance {
  teacherId: string
  teacherName: string
  year: number
  month: number
  timeZone: string
  asOf: number
  lateness: PerformanceRate
  absence: PerformanceRate
  summaryCompletion: PerformanceRate
  summaryTimeliness: PerformanceRate
  cancellation: PerformanceRate
  unknownLatenessCount: number
  unknownSummaryTimingCount: number
  pendingSummaryCount: number
  unsettledCount: number
  records: PerformanceRecord[]
  /** 老师主动发的 Moment：已完成的时间段 / 已判定的时间段（每月 10 段、每段每个学生至少 1 条，过期不能补） */
  momentSent: PerformanceRate
  /** 已有时间段被判定的学生数 */
  momentStudentCount?: number
  /** 当前时间段里还没发的学生数，以及这一段结束的时间 */
  momentPendingStudents?: number
  momentWindowEndsAt?: number | null
  momentSentCount: number
  /** 48 小时内回复的学生回合 / 已过回复期的回合 */
  momentReply: PerformanceRate
  pendingReplyCount: number
  score?: TeacherPerformanceScore | null
}
export type ScorePartKey = 'lessons' | 'absence' | 'momentSent' | 'momentReply'
/** 总分的组成部分；缺席扣分的 points 是负数。 */
export interface ScorePart { key: ScorePartKey; points: string | null; maxPoints: number; eligibleCount: number; excludedReason?: string | null }
export interface TeacherPerformanceScore { policyVersion: string; value: number | null; parts?: ScorePart[]; pendingReplyCount?: number }

/** 奖金比例只看总分：90–94 分 5%，95–100 分 10%。 */
export function bonusRate(score: number | null | undefined): number {
  return score == null ? 0 : score >= 95 ? 10 : score >= 90 ? 5 : 0
}
/** 「74.3 / 75」；缺席扣分没有上限，显示「−3.0」。 */
export function formatPart(part: ScorePart): string {
  if (part.points == null) return '—'
  const shown = (Math.round(Number(part.points) * 10) / 10).toFixed(1).replace('-', '−')
  return part.maxPoints > 0 ? `${shown} / ${part.maxPoints}` : shown
}
/** 还有没定下来的数据（出勤待判定、总结或 Moment 还在期限内），分数和奖金可能还会变。 */
export function hasPendingData(report: TeacherPerformance): boolean {
  return report.unsettledCount > 0 || report.pendingSummaryCount > 0 || report.pendingReplyCount > 0
}

export type LossKey = 'absence' | 'lateness' | 'summaryCompletion' | 'summaryTimeliness' | 'cancellation' | 'momentSent' | 'momentReply'
export interface PointLoss { key: LossKey; count: number; points: number }
/**
 * 本月各项丢了多少分，从多到少。与后端 2026-10-v4 规则一致，只用来解释；总分永远以后端为准。
 * 与老师端绩效页「How to improve」的排序相同。
 */
export function pointLosses(report: TeacherPerformance): PointLoss[] {
  const lessons = report.cancellation.denominator
  const unit = lessons > 0 ? 75 / lessons : 0
  const absent = report.absence.numerator
  const missing = Math.max(0, report.summaryCompletion.denominator - report.summaryCompletion.numerator - report.pendingSummaryCount)
  const lateSummaries = Math.max(0, report.summaryTimeliness.denominator - report.summaryTimeliness.numerator - missing)
  const absencePart = Number(report.score?.parts?.find(part => part.key === 'absence')?.points ?? 0)
  const sent = report.momentSent, reply = report.momentReply
  const losses: PointLoss[] = [
    { key: 'absence', count: absent, points: absent * unit - absencePart },
    { key: 'lateness', count: report.lateness.numerator, points: report.lateness.numerator * 0.3 * unit },
    { key: 'summaryCompletion', count: missing, points: missing * 0.5 * unit },
    { key: 'summaryTimeliness', count: lateSummaries, points: lateSummaries * 0.2 * unit },
    { key: 'cancellation', count: report.cancellation.numerator, points: report.cancellation.numerator * 0.5 * unit },
    { key: 'momentSent', count: sent.denominator - sent.numerator,
      points: sent.denominator ? 15 * (1 - sent.numerator / sent.denominator) : 0 },
    { key: 'momentReply', count: reply.denominator - reply.numerator,
      points: reply.denominator ? 10 * (1 - reply.numerator / reply.denominator) : 0 },
  ]
  return losses.filter(loss => loss.count > 0 && loss.points >= 0.05)
    .map(loss => ({ ...loss, points: Math.round(loss.points * 10) / 10 }))
    .sort((a, b) => b.points - a.points)
}
/** 没有分数（本月没有到期的课）排最后，升降序都一样。 */
export function compareScores(a: TeacherPerformance, b: TeacherPerformance, order?: string | null): number {
  const x = a.score?.value ?? null, y = b.score?.value ?? null
  const direction = order === 'descend' ? -1 : 1
  if (x == null) return y == null ? 0 : direction
  if (y == null) return -direction
  return x - y
}
export function formatRate(rate: PerformanceRate, locale: string): string {
  return rate.value == null || rate.denominator === 0 ? '—'
    : new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(rate.value)
}
export function compareRates(a: PerformanceRate, b: PerformanceRate, order?: string | null): number {
  // Ant Design reverses the comparator for descending order. Unknown values stay last both ways.
  const direction = order === 'descend' ? -1 : 1
  if (a.value == null) return b.value == null ? 0 : direction
  if (b.value == null) return -direction
  return a.value - b.value
}
export function filterTeachers(rows: TeacherPerformance[], query: string) {
  const q = query.trim().toLocaleLowerCase()
  return rows.filter(row => row.teacherId.includes(q) || row.teacherName.toLocaleLowerCase().includes(q))
}
export type RecordFilter = 'all' | MetricKey
export function filterRecords(records: PerformanceRecord[], filter: RecordFilter) {
  return records.filter(record => {
    switch (filter) {
      case 'lateness': return record.late === true
      case 'absence': return record.outcome === 'TEACHER_ABSENT'
      case 'summaryCompletion': return record.summaryRequired && !record.summarySubmitted
      case 'summaryTimeliness': return record.summaryTiming === 'LATE' || record.summaryTiming === 'MISSING'
      case 'cancellation': return record.outcome === 'TEACHER_CANCELLED'
      default: return true
    }
  })
}
export function formatTime(value: number | null, zone: string, locale: string) {
  if (value == null || !Number.isFinite(value)) return '—'
  const zoneName = zone.trim()
  const offset = /^(?:UTC|GMT|UT)?\s*([+-])(\d{1,2})(?::?(\d{2}))?(?::?(\d{2}))?$/i.exec(zoneName)
  let displayValue = value
  let displayZone = /^(UTC|GMT|UT|Z)$/i.test(zoneName) ? 'UTC' : zoneName
  if (offset) {
    const hours = Number(offset[2]), minutes = Number(offset[3] ?? 0), seconds = Number(offset[4] ?? 0)
    if (hours > 18 || minutes > 59 || seconds > 59 || (hours === 18 && (minutes !== 0 || seconds !== 0))) return '—'
    // Kotlin returns fixed zones such as UTC+09:00, which browsers may reject.
    // Shift only the displayed instant and format in UTC; do not depend on Intl offset support.
    displayValue += (offset[1] === '-' ? -1 : 1) * (hours * 3600 + minutes * 60 + seconds) * 1000
    displayZone = 'UTC'
  }
  try {
    return new Intl.DateTimeFormat(locale, { timeZone: displayZone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(displayValue)
  } catch {
    // Unknown zones or invalid dates must not take down the report or silently change its timezone.
    return '—'
  }
}
